import {
  useAllIngredients,
  useAllOutOfStockIngredients,
  useAllOutOfStockItemsByShopExternalId,
  useAllOutOfStockProducts,
  useAllProducts,
} from '../../../apis/products-api';
import {
  IngredientDto,
  OutOfStockIngredientDto,
  OutOfStockProductDto,
  ProductDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { dedupeByWarehouseItemId, isNotArchived, isScheduleInEffect } from '../../../shared/utils/outOfStockFilters';

export type OutOfStockItem = {
  name: string;
  added: number;
  id: number;
  isActive: boolean;
  // End of the scheduled out-of-stock period (ISO string), or null/undefined when open-ended.
  scheduledUntil?: string | null;
  // Warehouse item the ingredient maps to; several ingredients can share one. Undefined for products.
  warehouseItemId?: string | null;
};

type ProductById = Map<number, ProductDto>;
type IngredientById = Map<number, IngredientDto>;

const indexProducts = (products: ProductDto[] | undefined): ProductById => {
  const map: ProductById = new Map();
  for (const product of products ?? []) {
    if (product.productId != null) map.set(product.productId, product);
  }
  return map;
};

const indexIngredients = (ingredients: IngredientDto[] | undefined): IngredientById => {
  const map: IngredientById = new Map();
  for (const ingredient of ingredients ?? []) {
    if (ingredient.ingredientId != null) map.set(ingredient.ingredientId, ingredient);
  }
  return map;
};

// Records whose product or ingredient is missing or archived are dropped: there is no name to show.
const toOutOfStockItems = (
  outOfStockProducts: OutOfStockProductDto[],
  outOfStockIngredients: OutOfStockIngredientDto[],
  productById: ProductById,
  ingredientById: IngredientById
): OutOfStockItem[] => {
  const items: OutOfStockItem[] = [];
  const currentTime = dayjs();

  for (const outOfStockProduct of outOfStockProducts) {
    const product = outOfStockProduct.productId != null ? productById.get(outOfStockProduct.productId) : undefined;
    if (product && isNotArchived(product) && outOfStockProduct.added) {
      items.push({
        id: product.productId ?? -1,
        name: product.name,
        added: currentTime.diff(dayjs(outOfStockProduct.added), 'day'),
        isActive: product.active,
        scheduledUntil: outOfStockProduct.scheduledUntil,
      });
    }
  }

  for (const outOfStockIngredient of outOfStockIngredients) {
    const ingredient =
      outOfStockIngredient.ingredientId != null ? ingredientById.get(outOfStockIngredient.ingredientId) : undefined;
    if (ingredient && isNotArchived(ingredient) && outOfStockIngredient.added) {
      items.push({
        id: ingredient.ingredientId ?? -1,
        name: ingredient.name,
        added: currentTime.diff(dayjs(outOfStockIngredient.added), 'day'),
        isActive: Boolean(ingredient.isActive),
        scheduledUntil: outOfStockIngredient.scheduledUntil,
        warehouseItemId: ingredient.warehouseItemId,
      });
    }
  }

  return items;
};

export const useRestaurantData = (): {
  getRestaurantOutOfStockProducts: (restaurantId: string) => OutOfStockItem[];
  isLoading: boolean;
} => {
  const { data: outOfStockProducts, isLoading: isOutOfStockProductsLoading } = useAllOutOfStockProducts();
  const { data: outOfStockIngredients, isLoading: isOutOfStockIngredientsLoading } = useAllOutOfStockIngredients();
  const { data: products, isLoading: isProductsLoading } = useAllProducts();
  const { data: ingredients, isLoading: isIngredientsLoading } = useAllIngredients();

  const isLoading =
    isOutOfStockProductsLoading || isOutOfStockIngredientsLoading || isProductsLoading || isIngredientsLoading;

  // Index the master catalogs by id and the out-of-stock records by shop up front, so resolving one
  // restaurant's items is linear in that restaurant's out-of-stock records rather than re-scanning
  // every catalog for every restaurant on each call (this hook is called once per restaurant row).
  const productById = useMemo(() => indexProducts(products), [products]);
  const ingredientById = useMemo(() => indexIngredients(ingredients), [ingredients]);

  const outOfStockProductsByShop = useMemo(() => {
    const map = new Map<string, NonNullable<typeof outOfStockProducts>[number][]>();
    for (const item of outOfStockProducts ?? []) {
      if (!item.externalShopId) continue;
      const existing = map.get(item.externalShopId);
      if (existing) existing.push(item);
      else map.set(item.externalShopId, [item]);
    }
    return map;
  }, [outOfStockProducts]);

  const outOfStockIngredientsByShop = useMemo(() => {
    const map = new Map<string, NonNullable<typeof outOfStockIngredients>[number][]>();
    for (const item of outOfStockIngredients ?? []) {
      if (!item.externalShopId) continue;
      const existing = map.get(item.externalShopId);
      if (existing) existing.push(item);
      else map.set(item.externalShopId, [item]);
    }
    return map;
  }, [outOfStockIngredients]);

  const getRestaurantOutOfStockProducts = useCallback(
    (restaurantId: string): OutOfStockItem[] => {
      if (!outOfStockProducts || !outOfStockIngredients) return [];

      return toOutOfStockItems(
        outOfStockProductsByShop.get(restaurantId) ?? [],
        outOfStockIngredientsByShop.get(restaurantId) ?? [],
        productById,
        ingredientById
      );
    },
    [
      outOfStockProducts,
      outOfStockIngredients,
      outOfStockProductsByShop,
      outOfStockIngredientsByShop,
      productById,
      ingredientById,
    ]
  );

  return {
    getRestaurantOutOfStockProducts,
    isLoading,
  };
};

/**
 * The shop's items out of stock right now: active, not past their scheduled end, and collapsed by warehouse item
 * (several ingredients can map to one).
 */
export const useCurrentShopOutOfStockItems = (
  shopExternalId: string,
  refetchOnMount?: boolean | 'always'
): {
  items: OutOfStockItem[];
  isLoading: boolean;
} => {
  // isInitialLoading: a disabled query (no shop id yet) reports isLoading forever.
  const { data: outOfStock, isInitialLoading: isOutOfStockLoading } =
    useAllOutOfStockItemsByShopExternalId(shopExternalId, refetchOnMount);
  const { data: products, isInitialLoading: isProductsLoading } = useAllProducts();
  const { data: ingredients, isInitialLoading: isIngredientsLoading } = useAllIngredients();

  const productById = useMemo(() => indexProducts(products), [products]);
  const ingredientById = useMemo(() => indexIngredients(ingredients), [ingredients]);

  const items = useMemo(() => {
    if (!outOfStock) return [];
    const all = toOutOfStockItems(
      outOfStock.outOfStockProducts,
      outOfStock.outOfStockIngredients,
      productById,
      ingredientById
    );
    return dedupeByWarehouseItemId(all.filter((item) => item.isActive && isScheduleInEffect(item)));
  }, [outOfStock, productById, ingredientById]);

  return { items, isLoading: isOutOfStockLoading || isProductsLoading || isIngredientsLoading };
};
